import { create, type StateCreator } from 'zustand'
import { devtools } from 'zustand/middleware'
import { zustandPlugin } from '@/modules/shared/openreplay'

type State = {
  /** Ile pozycji jest zaznaczonych w aktywnej zakładce panelu admina. */
  selectedCount: number
  /**
   * Licznik żądań usunięcia. Zakładka nasłuchuje jego zmiany zamiast być wołana
   * przez panel po referencji do instancji.
   */
  removeRequestId: number
}

type Actions = {
  setSelectedCount: (count: number) => void
  requestRemove: () => void
  clearSelection: () => void
}

/** Ten sam cast co w `genericErrorStore` - przywraca trzeci argument `set`. */
type StoreMutators = [['zustand/devtools', never]]

const trackedCreator = zustandPlugin as unknown as <T>(
  creator: StateCreator<T, StoreMutators, []>,
  name?: string,
) => StateCreator<T, StoreMutators, []>

/**
 * Zaznaczenie w panelu admina.
 *
 * Panel trzymał referencje do instancji zakładek tylko po to, żeby wywołać na nich
 * `removeCheckedItems()`, a zakładki oddawały liczbę zaznaczonych propem `onItemChecked`.
 * Oba kierunki idą teraz przez ten store, więc panel nie musi znać instancji, a zakładki
 * mogą być zwykłymi komponentami funkcyjnymi.
 */
export const useAdminSelectionStore = create<State & Actions>()(
  devtools(
    trackedCreator<State & Actions>(
      (set) => ({
        selectedCount: 0,
        removeRequestId: 0,
        setSelectedCount: (selectedCount) =>
          set({ selectedCount }, false, 'setSelectedCount'),
        requestRemove: () =>
          set(
            (s) => ({ removeRequestId: s.removeRequestId + 1 }),
            false,
            'requestRemove',
          ),
        clearSelection: () =>
          set({ selectedCount: 0 }, false, 'clearSelection'),
      }),
      'admin-selection',
    ),
    { name: 'EasyTacho', store: 'admin-selection' },
  ),
)

/** Poza Reactem i w komponentach klasowych, gdzie nie ma hooków. */
export const setAdminSelectedCount = (count: number) =>
  useAdminSelectionStore.getState().setSelectedCount(count)

export const clearAdminSelection = () =>
  useAdminSelectionStore.getState().clearSelection()

export const requestAdminRemove = () =>
  useAdminSelectionStore.getState().requestRemove()

/**
 * Woła `handler` przy każdym żądaniu usunięcia. Zwraca funkcję odsubskrybowania -
 * zakładki podpinają to w `componentDidMount` i zdejmują w `componentWillUnmount`.
 */
export const subscribeToAdminRemove = (handler: () => void) =>
  useAdminSelectionStore.subscribe((state, prev) => {
    if (state.removeRequestId !== prev.removeRequestId) handler()
  })

/** Zmiany liczby zaznaczonych - dla panelu, który jest jeszcze klasą. */
export const subscribeToAdminSelectedCount = (
  handler: (count: number) => void,
) =>
  useAdminSelectionStore.subscribe((state, prev) => {
    if (state.selectedCount !== prev.selectedCount) handler(state.selectedCount)
  })
